<template>
  <v-container>
    <v-expansion-panels variant="accordion" class="mb-4">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <span class="text-h6 font-weight-medium text-primary"
            >Fiche Laverie {{ laverie.numeroDeContrat }}</span
          >
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-card class="mb-4">
            <v-card-text>
              <v-text-field v-model="laverie.name" label="Nom" density="compact" />
              <v-text-field
                v-model="laverie.fullAddress"
                label="Adresse"
                density="compact"
              />
              <v-text-field
                v-model="laverie.telephonePrincipal"
                label="Contact principal"
                density="compact"
              />
              <v-text-field
                v-model="laverie.telephoneAutre"
                label="Contact secondaire"
                density="compact"
              />
              <v-text-field
                v-model="laverie.codeAcces"
                label="Code d'accès"
                density="compact"
              />
            </v-card-text>
          </v-card>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <div v-if="!isInterventionStarted" class="d-flex justify-center mb-4">
      <v-btn
        color="primary"
        class="text-white"
        min-width="120"
        @click="startIntervention()"
      >
        Démarrer l'intervention
      </v-btn>
    </div>

    <!-- ACTIVITE -->
    <v-expansion-panels variant="accordion" class="mb-4">
      <v-expansion-panel :disabled="!isInterventionStarted">
        <v-expansion-panel-title>
          <span class="text-h6 font-weight-medium text-primary">Activité</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-card class="mb-4">
            <v-card-text>
              <v-text-field
                label="Type d'activité"
                :items="activityTypes"
                v-model="task.taskType"
              />

              <v-select label="Statut" :items="statusItems" v-model="task.taskStatus" />
              <v-textarea label="Description" v-model="task.subject" />
            </v-card-text>
          </v-card>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- PRODTUIS -->
    <v-expansion-panels variant="accordion" class="mb-4">
      <v-expansion-panel :disabled="!isInterventionStarted">
        <v-expansion-panel-title>
          <span class="text-h6 font-weight-medium text-primary">Produits</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-expansion-panels multiple>
            <v-expansion-panel
              :disabled="!isInterventionStarted"
              v-for="product in _products"
              :key="product.id"
            >
              <!-- PRODUCT HEADER -->
              <v-expansion-panel-title>
                {{ product.aquaNb }} – {{ product.productType }} –
                {{ product.productMake }}
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <div v-if="getIntervention(product.id)" class="mb-6">
                  <!-- INTERVENTION HEADER -->
                  <div class="d-flex justify-space-between align-center mb-4">
                    <div>
                      <span class="text-subtitle-1 font-weight-bold"> Intervention </span>

                      <v-chip
                        size="small"
                        :color="
                          getIntervention(product.id).status === 'COMPLETED'
                            ? 'success'
                            : getIntervention(product.id).status === 'IN_PROGRESS'
                            ? 'warning'
                            : 'grey'
                        "
                        class="ml-2"
                      >
                        {{ getIntervention(product.id).status }}
                      </v-chip>
                    </div>

                    <v-btn
                      v-if="!isInterventionCompleted(getIntervention(product.id))"
                      size="small"
                      color="primary"
                      @click="completeIntervention(getIntervention(product.id))"
                    >
                      Terminer
                    </v-btn>
                  </div>

                  <!-- CHECKLIST ITEMS -->
                  <v-card
                    v-for="item in getIntervention(product.id).items"
                    :key="item.id"
                    variant="outlined"
                    class="mb-3 pa-3"
                  >
                    <!-- CHECKBOX -->
                    <v-checkbox
                      v-if="item.itemType === 'CHECKBOX'"
                      v-model="item.completed"
                      :label="item.label"
                      hide-details
                    />

                    <!-- NUMERIC -->
                    <v-text-field
                      v-if="item.itemType === 'NUMERIC'"
                      type="number"
                      :label="item.label"
                      v-model="item.numericValue"
                      density="comfortable"
                    />

                    <!-- TEXT -->
                    <v-textarea
                      v-if="item.itemType === 'TEXT'"
                      :label="item.label"
                      v-model="item.textValue"
                      auto-grow
                    />

                    <!-- COMMENT -->
                    <v-text-field
                      label="Commentaire"
                      v-model="item.comment"
                      density="compact"
                    />
                  </v-card>

                  <v-divider class="my-4" />
                </div>

                <!-- ADD PANNE (shown only if no pending panne) -->
                <div
                  v-if="!pannesPerProduct[product.id]?.some((p) => !p.id)"
                  class="d-flex justify-center my-3"
                >
                  <v-btn color="primary" class="text-white" @click="addPanne(product)">
                    Ajouter panne
                  </v-btn>
                </div>

                <!-- PANNES -->
                <v-expansion-panels
                  variant="accordion"
                  v-model="openedPanneIndex[product.id]"
                >
                  <v-expansion-panel
                    v-for="(panne, i) in pannesPerProduct[product.id] || []"
                    :key="panne.id ?? i"
                    :value="i"
                    :class="{ 'opacity-50': panne.fixed }"
                  >
                    <!-- PANNE HEADER -->
                    <v-expansion-panel-title>
                      <div class="d-flex align-center justify-space-between w-100">
                        <span>
                          {{ panne.desc || "Nouvelle panne" }}
                        </span>

                        <v-checkbox
                          v-model="panne.fixed"
                          label="Réparée"
                          density="compact"
                          hide-details
                          @click.stop
                        />
                      </div>
                    </v-expansion-panel-title>

                    <!-- PANNE BODY -->
                    <v-expansion-panel-text>
                      <v-text-field
                        label="Description panne"
                        v-model="panne.desc"
                        :disabled="panne.fixed"
                      />

                      <!-- INSTALLED SPAREPARTS -->
                      <div
                        v-for="(installedSparepart, j) in panne.installedSpareparts || []"
                        :key="j"
                        class="mb-2"
                      >
                        <v-autocomplete
                          label="Pièces détachées"
                          :items="spareParts"
                          v-model="installedSparepart.sparepartId"
                          item-title="displayName"
                          item-value="id"
                          multiple
                          :disabled="panne.fixed"
                        />

                        <v-text-field
                          type="number"
                          label="Quantité"
                          v-model="installedSparepart.quantity"
                          :disabled="panne.fixed"
                        />
                      </div>

                      <!-- ADD INSTALLED SPAREPART -->
                      <div class="d-flex justify-end mt-2">
                        <v-btn
                          size="small"
                          variant="outlined"
                          color="primary"
                          :disabled="panne.fixed"
                          @click="addInstalledSparepart(panne)"
                        >
                          Ajouter pièce détachée
                        </v-btn>
                      </div>

                      <!-- SUBMIT PANNE (only when no id) -->
                      <div v-if="!panne.id" class="d-flex justify-center mt-4">
                        <v-btn
                          color="primary"
                          class="text-white"
                          min-width="120"
                          @click="submitPanne(product, panne)"
                        >
                          Soumettre
                        </v-btn>

                        <v-spacer style="max-width: 16px" />

                        <v-btn
                          color="error"
                          class="text-white"
                          min-width="120"
                          @click="cancelPanne(product, i)"
                        >
                          Annuler
                        </v-btn>
                      </div>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- PHOTOS -->
    <v-expansion-panels variant="accordion" class="mb-4">
      <v-expansion-panel :disabled="!isInterventionStarted">
        <v-expansion-panel-title>
          <span class="text-h6 font-weight-medium text-primary">Photos</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-card class="mt-4">
            <v-card-text>
              <v-file-input multiple label="Avant intervention" v-model="photosBefore" />
              <v-file-input multiple label="Après intervention" v-model="photosAfter" />
            </v-card-text>
          </v-card>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- OBSERVATION -->
    <v-expansion-panels variant="accordion" class="mb-4">
      <v-expansion-panel :disabled="!isInterventionStarted">
        <v-expansion-panel-title>
          <span class="text-h6 font-weight-medium text-primary">Observation</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-textarea class="mt-4" v-model="task.observation" />
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <div
      v-if="!isInterventionEnded && isInterventionStarted"
      class="d-flex justify-center mb-4"
    >
      <v-btn
        color="primary"
        class="text-white"
        min-width="120"
        @click="endIntervention()"
      >
        Terminer l'intervention
      </v-btn>
    </div>

    <!-- SIGNATURE -->
    <v-expansion-panels :disabled="!isInterventionEnded" variant="accordion" class="mb-4">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <span class="text-h6 font-weight-medium text-primary"> Signature client </span>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <v-card variant="outlined" rounded="lg" class="pa-4">
            <div class="d-flex justify-center mb-4">
              <SignaturePad v-if="!signatureFile" ref="signature" />

              <v-img v-else :src="signatureUrl" max-width="400" max-height="400" />
            </div>

            <div v-if="!signatureFile" class="d-flex justify-end">
              <v-btn variant="outlined" class="mr-2" @click="clearSignature">
                Annuler
              </v-btn>

              <v-btn
                color="primary"
                class="text-white"
                :disabled="showProgressBar"
                @click="saveSignature"
              >
                Enregistrer
              </v-btn>
            </div>
          </v-card>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- ACTIONS -->
    <v-row v-if="laverie.email" class="mt-6">
      <v-col>
        <v-btn :disabled="!isInterventionEnded" color="success" block @click="sendEmail">
          Envoyer email
        </v-btn>
      </v-col>
    </v-row>
    <UploadProgress
      :visible="showProgressBar"
      :file-name="progressInfo.fileName"
      :percentage="progressInfo.percentage"
    />
  </v-container>
</template>
<script setup lang="ts">
import { useAppStore, useSessionStore } from "@/store";
import { Panne, Product, ProgressInfo, RefEntityType, VaultFileType } from "@/types";
import { Laverie } from "@/types";
import { Task } from "@/types";
import { fetchVaultFiles, uploadTaskSignature } from "@/utils/vault-file-api";
import { getLaverieById } from "@/utils/laverie-api";
import { getProductsByLaverieId } from "@/utils/product-api";
import { getTaskById, setEndDateTime, setStartDateTime } from "@/utils/task-api";
import { AxiosProgressEvent } from "axios";
import { ref, reactive, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import SignaturePad from "vue3-signature";
import { VaultFile } from "@/types/vault-file";
import UploadProgress from "@/components/UploadProgress.vue";
import { instance } from "@/utils/axios-service";

const activityTypes = ["Panne", "Maintenance"];
const statusItems = ["En cours", "Terminé", "Reporté"];

const spareParts = [
  { title: "Pompe vidange", value: 1 },
  { title: "Courroie", value: 2 },
  { title: "Carte électronique", value: 3 },
];

const route = useRoute();
const sessionStore = useSessionStore();
const appStore = useAppStore();

const task = ref<Task>({} as Task);
const laverie = ref<Laverie>({} as Laverie);
const products = ref<Array<Product>>([]);
const pannesPerProduct = ref<Record<string, Panne[]>>({});
const openedPanneIndex = ref<Record<string, number | null>>({});
const showProgressBar = ref(false);

const progressInfo = ref<ProgressInfo>({
  fileName: "",
  percentage: 0,
});

const photosBefore = ref([]);
const photosAfter = ref([]);
const signature = ref(null);
const signatureFile = ref<VaultFile | null>(null);
const signatureUrl = ref(null);

const isInterventionStarted = computed(() => {
  return task.value && task.value.startDateTime ? true : false;
});

const isInterventionEnded = computed(() => {
  return task.value && task.value.endDateTime ? true : false;
});

onMounted(async () => {
  if (!route.params.id) {
    return;
  }

  task.value = await getTaskById(route.params.id as string);
  laverie.value = await getLaverieById(task.value.laverieId);
  products.value = await getProductsByLaverieId(laverie.value.id);

  const files = await fetchVaultFiles({
    refEntityType: RefEntityType.TASK,
    refEntityId: task.value.id,
    type: VaultFileType.SIGNATURE,
  });

  signatureFile.value = files[0] ?? null;
  console.log("signatureFile", signatureFile.value);
  if (signatureFile.value) {
    await loadSignature(signatureFile.value.id);
  }
});

function startIntervention() {
  try {
    task.value.startDateTime = new Date();
    setStartDateTime(task.value.id, task.value.startDateTime);
  } catch (e) {
    task.value.startDateTime = null;
  }
}

async function endIntervention() {
  try {
    task.value.endDateTime = new Date();
    await setEndDateTime(task.value.id, task.value.endDateTime);
  } catch (e) {
    task.value.endDateTime = null;
  }
}

function clearSignature() {
  signature.value?.clear?.();
}

async function saveSignature() {
  if (!signature.value || signature.value.isEmpty()) {
    return;
  }

  showProgressBar.value = true;
  progressInfo.value.fileName = "signature.png";
  progressInfo.value.percentage = 0;

  try {
    const response = await uploadTaskSignature(
      signature.value.toDataURL("image/png"),
      task.value.id,
      (event: AxiosProgressEvent) => {
        if (event.progress !== undefined) {
          progressInfo.value.percentage = Math.round(event.progress * 100);
        }
      }
    );
    signatureFile.value = response.data;
    if (signatureFile.value) {
      await loadSignature(signatureFile.value.id);
    }
    appStore.sendSuccessNotice("Signature enregistrée avec succès");
  } catch (e) {
    // Ignore
  } finally {
    showProgressBar.value = false;
  }
}

async function loadSignature(fileId: string) {
  const response = await instance.get(`/vault-files/${fileId}/content`, {
    responseType: "blob",
  });
  const blob = new Blob([response.data], { type: response.headers["content-type"] });
  signatureUrl.value = URL.createObjectURL(blob);
}

function addPanne(product: Product): void {
  if (!pannesPerProduct.value[product.id]) {
    pannesPerProduct.value[product.id] = [];
  }

  pannesPerProduct.value[product.id].push({
    date: new Date().toISOString().split("T")[0],
    desc: "",
    createTask: true,
    installedSpareparts: [],
    technicien: sessionStore.loggedUser.username,
  });

  openedPanneIndex.value[product.id] = pannesPerProduct.value[product.id].length - 1;
}

function cancelPanne(product: Product, panneIndex: number): void {
  pannesPerProduct.value[product.id].splice(panneIndex, 1);
  openedPanneIndex.value[product.id] = null;
}
async function submitPanne(product: Product, panne: Panne): Promise<void> {
  openedPanneIndex.value[product.id] = null;
}

function addInstalledSparepart(panne: Panne): void {
  if (!panne.installedSpareparts) {
    panne.installedSpareparts = [];
  }

  panne.installedSpareparts.push({
    sparepartId: "",
    taskId: task.value.id,
    panneId: panne.id!,
    quantity: 1,
  });
}

const sendEmail = () => {
  console.log("Send Email");
};

// Fake products
const _products = ref([
  { id: 1, aquaNb: "ML-01", productType: "MACHINE_A_LAVER", productMake: "Electrolux" },
  { id: 2, aquaNb: "SL-02", productType: "SECHE_LINGE", productMake: "Whirlpool" },
]);

// Fake interventions
const interventions = ref([
  {
    id: 101,
    productId: 1,
    status: "IN_PROGRESS",
    items: [
      {
        id: 1,
        label: "Relever les compteurs",
        itemType: "NUMERIC",
        numericValue: 1200,
        comment: "comment",
      },
      {
        id: 2,
        label: "Nettoyage pompe",
        itemType: "CHECKBOX",
        completed: false,
        comment: "comment",
      },
      {
        id: 3,
        label: "Commentaire",
        itemType: "TEXT",
        textValue: "",
        comment: "comment",
      },
    ],
  },
  {
    id: 102,
    productId: 2,
    status: "NOT_STARTED",
    items: [
      {
        id: 4,
        label: "Nettoyage filtre",
        itemType: "CHECKBOX",
        completed: true,
        comment: "comment",
      },
      {
        id: 5,
        label: "Essai cycle",
        itemType: "CHECKBOX",
        completed: false,
        comment: "comment",
      },
    ],
  },
]);

function getIntervention(productId) {
  return interventions.value.find((i) => i.productId === productId);
}

function isInterventionCompleted(intervention) {
  return intervention.items.every((item) => {
    if (item.itemType === "CHECKBOX") return item.completed;
    if (item.itemType === "NUMERIC") return item.numericValue != null;
    if (item.itemType === "TEXT") return item.textValue?.length > 0;
  });
}

function completeIntervention(intervention) {
  intervention.status = "COMPLETED";
}
</script>
